import { StaffRole } from '@urbanx/agx.auth.client'
import {
  Agency,
  UrbanXTeamByAgenciesResponse,
} from '@urbanx/agx.agencies.client'
import { States } from '@urbanx/agx.staff.client'

import { StaffMemberFormValues } from './StaffMemberFormPage'
import { isEmailValid } from 'helpers/emailHelper'

export const requiredStaffFields: Array<keyof StaffMemberFormValues> = [
  'email',
  'firstName',
  'lastName',
  'roles',
  'states',
  'title',
]

export const rolesAssignableToAgency = [
  StaffRole.AdminManager,
  StaffRole.BusinessManager,
  StaffRole.MarketingExpert,
].map((role) => role.toString())

export const rolesWithStateAccess = [
  StaffRole.ListingsContributor,
  StaffRole.LaunchContributor,
  StaffRole.ContractsContributor,
  StaffRole.TransactionsContributor,
  StaffRole.FinanceContributor,
].map((role) => role.toString())

export const staffStateOptions = [
  { value: States.NSW, label: 'NSW' },
  { value: States.QLD, label: 'QLD' },
]

export type StaffRoleOption = { label: string; value: string | StaffRole }

export const getAvailableStaffRoleOptions = (
  staffRoleOptions: StaffRoleOption[],
  doesUserHaveRequiredRoles: (requiredRoles: StaffRole[] | undefined) => boolean
) => {
  const hasSuperAdminOrExecutive = doesUserHaveRequiredRoles([
    StaffRole.SuperAdmin,
    StaffRole.Executive,
  ])
  const hasBusinessManager = doesUserHaveRequiredRoles([
    StaffRole.BusinessManager,
  ])

  const restrictedRoles = [
    ...(!hasSuperAdminOrExecutive
      ? [StaffRole.SuperAdmin, StaffRole.Executive]
      : []),
    ...(!hasSuperAdminOrExecutive && !hasBusinessManager
      ? [StaffRole.BusinessManager]
      : []),
  ]

  return staffRoleOptions.filter(
    (role) => !restrictedRoles.includes(role.value as StaffRole)
  )
}

export const validateStaffMemberForm = (
  form: StaffMemberFormValues,
  showStateSelect: boolean
) => {
  const errors = requiredStaffFields.reduce(
    (acc, field) => {
      if (!showStateSelect && field === 'states') return acc

      const value = form[field]
      if (field === 'email' && !isEmailValid(value as string)) {
        acc.push(field)
      } else if (value === '' || value === undefined || value === null) {
        acc.push(field)
      }

      return acc
    },
    [] as Array<keyof StaffMemberFormValues>
  )

  if (!form.roles || form.roles.length === 0) {
    errors.push('roles')
  }

  if (showStateSelect && (!form.states || form.states.length === 0)) {
    errors.push('states')
  }

  return errors.map(String)
}

export const mapAgenciesToOptions = (
  agencies: Agency[] | undefined,
  isPending: boolean
) => {
  return !isPending
    ? (agencies?.map((agency) => ({
        label: agency.name,
        value: agency.id,
      })) ?? [])
    : []
}

export const dedupeRoleConflicts = (
  roleConflicts: UrbanXTeamByAgenciesResponse[],
  excludedStaffMemberId?: string
) => {
  return roleConflicts
    .map((roleConflict) => {
      const uniqueAssignments =
        roleConflict.staffRoleAssignments?.filter((assignment, index, arr) => {
          const key = `${assignment.agencyId}-${assignment.staffMemberId}`
          return (
            arr.findIndex((x) => `${x.agencyId}-${x.staffMemberId}` === key) ===
            index
          )
        }) ?? []

      return {
        ...roleConflict,
        staffRoleAssignments: uniqueAssignments.filter(
          (x) => x.staffMemberId !== excludedStaffMemberId
        ),
      } as UrbanXTeamByAgenciesResponse
    })
    .filter(
      (roleConflict) => (roleConflict.staffRoleAssignments?.length ?? 0) > 0
    )
}
